import React from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

const SearchResults = ({ searchQuery, results, onClose, onResultClick = onClose, isVisible }) => {
  if (!isVisible || !searchQuery.trim()) return null;

  const groupedResults = {
    products: results.filter(item => item.type === 'product'),
    courses: results.filter(item => item.type === 'course'),
    categories: results.filter(item => item.type === 'category')
  };

  const totalResults = results.length;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={onClose}>
      <div className="absolute top-20 left-1/2 transform -translate-x-1/2 w-full max-w-4xl max-h-[70vh] overflow-y-auto bg-secondary border border-primary/20 rounded-lg shadow-2xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <MagnifyingGlassIcon className="h-5 w-5 text-primary" />
              <span className="text-black font-medium">
                Search results for "{searchQuery}"
              </span>
              <span className="text-gray-800 text-sm">({totalResults} results)</span>
            </div>
            <button
              onClick={onClose}
              className="text-gray-800 hover:text-black transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Results */}
          {totalResults === 0 ? (
            <div className="text-center py-8">
              <MagnifyingGlassIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-800">No results found for "{searchQuery}"</p>
              <p className="text-gray-700 text-sm mt-2">Try different keywords or check your spelling</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Products */}
              {groupedResults.products.length > 0 && (
                <div>
                  <h3 className="text-primary font-semibold mb-3 flex items-center">
                    <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                    Products ({groupedResults.products.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {groupedResults.products.map((product) => (
                      <Link
                        key={product.id}
                        to={`/product/${product.id}`}
                        onClick={onResultClick}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary transition-colors group"
                      >
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded-md"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-black font-medium truncate group-hover:text-primary transition-colors">
                            {product.name}
                          </h4>
                          <p className="text-gray-800 text-sm truncate">
                            {product.shortDescription}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Courses */}
              {groupedResults.courses.length > 0 && (
                <div>
                  <h3 className="text-primary font-semibold mb-3 flex items-center">
                    <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                    Courses ({groupedResults.courses.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {groupedResults.courses.map((course) => (
                      <Link
                        key={course.id}
                        to={`/digital-bundle/${course.id}`}
                        onClick={onClose}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary transition-colors group"
                      >
                        <img
                          src={course.image}
                          alt={course.name}
                          className="w-12 h-12 object-cover rounded-md"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-black font-medium truncate group-hover:text-primary transition-colors">
                            {course.name}
                          </h4>
                          <p className="text-gray-800 text-sm truncate">
                            {course.shortDescription}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Categories */}
              {groupedResults.categories.length > 0 && (
                <div>
                  <h3 className="text-primary font-semibold mb-3 flex items-center">
                    <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                    Categories ({groupedResults.categories.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {groupedResults.categories.map((category) => (
                      <Link
                        key={category.name}
                        to={`/products?category=${encodeURIComponent(category.name)}`}
                        onClick={onClose}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary transition-colors group"
                      >
                        <div className="w-12 h-12 bg-primary/20 rounded-md flex items-center justify-center">
                          <span className="text-primary font-bold text-lg">
                            {category.name.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-black font-medium truncate group-hover:text-primary transition-colors">
                            {category.name}
                          </h4>
                          <p className="text-gray-800 text-sm">
                            {category.count} items
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResults; 